/* eslint-disable import/no-anonymous-default-export */
import axios from "axios";
import host from "./host";

export const axiosInstance = axios.create();

// Interceptor para tratar respostas e erros
axiosInstance.interceptors.response.use(
  function (rs) {
    return rs;
  },
  function (err) {
    if (err?.response?.data?.redirect_to_login) {
      localStorage.removeItem("user");
      localStorage.removeItem('token');
      const redirectRoute = window.location.origin + '/login';
      window.location.href = redirectRoute;
    } else if (err?.response?.data?.error) {
      // Não faça alert aqui, deixe os componentes tratarem
      console.error(err?.response?.data?.error);
    }
    return Promise.reject(err);
  }
);

const path = {
  auth: "/api/auth-users",
  authUser: "/api/auth-users",
  users: "/api/users",
  clients: "/api/clients",
  batches: "/api/batches",
  dashboard: "/api/dashboard",
  attachments: "/api/attachments",
  enums: "/api/enums",
  drums: "/api/drums",
  operations: "/api/operations",
  machines: "/api/machines",
  temperatureReadings: "/api/temperature-readings",
};

export default {
  // Autenticação
  auth: {
    authenticate: body => {
      return axiosInstance.post(`${host}${path.auth}/authenticate-panel/`, body);
    },
    forgotPassword: email => {
      return axiosInstance.post(`${host}${path.auth}/reset-password/?email=${email}`);
    },
    changePassword: body => {
      return axiosInstance.put(`${host}${path.authUser}/change-password`, body);
    },
    changePasswordForced: body => {
      return axiosInstance.put(`${host}${path.authUser}/change-password-forced`, body);
    },
    resetPasswordForced: (body, token) => {
      return axiosInstance.put(`${host}${path.auth}/change-password-forced`, body, { 
        headers: { common: { ['Authorization']: `Bearer ${token}` } } 
      });
    },
    // Redefinição via token curto (15m) enviado por e-mail. O token vai no corpo;
    // não usa Bearer porque é um endpoint público de redefinição de senha.
    resetPasswordWithToken: body => {
      return axiosInstance.post(`${host}${path.auth}/reset-password-with-token`, body);
    },
    setAuthorizationToken: async (formattedToken) => {
      axiosInstance.defaults.headers.common.Authorization = formattedToken;
      localStorage.setItem('token', formattedToken);
      return;
    },
    removeAuthorizationToken: async () => {
      axiosInstance.defaults.headers.common.Authorization = null;
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return;
    },
    resetPassword: id => {
      return axiosInstance.post(`${host}${path.auth}/reset-password-painel/${id}`);
    },
    currentUser: () => {
      return axiosInstance.get(`${host}${path.auth}/current-user/`);
    },
  },

  // Usuários
  user: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.users}/list-panel-users`, { params });
    },
    listAdmin: (params) => {
      return axiosInstance.get(`${host}${path.users}/list-admin`, { params });
    },
    get: id => {
      return axiosInstance.get(`${host}${path.users}/app/${id}`);
    },
    create: body => {
      return axiosInstance.post(`${host}${path.users}/`, body);
    },
    update: (id, body) => {
      return axiosInstance.put(`${host}${path.users}/${id}`, body);
    },
    delete: id => {
      return axiosInstance.delete(`${host}${path.users}/${id}`);
    },
    activate: body => {
      return axiosInstance.post(`${host}${path.users}/activate-painel`, body);
    },
    inactivate: body => {
      return axiosInstance.post(`${host}${path.users}/inactivate-painel`, body);
    },
    // Operadores (Operador e Operador-comprador) - gestão pelo Admin
    listOperators: (params) => {
      return axiosInstance.get(`${host}${path.users}/operators`, { params });
    },
    createOperator: body => {
      return axiosInstance.post(`${host}${path.users}/operator`, body);
    },
  },

  // Clientes
  client: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.clients}/`, { params });
    },
    get: id => {
      return axiosInstance.get(`${host}${path.clients}/${id}`);
    },
    create: body => {
      return axiosInstance.post(`${host}${path.clients}`, body);
    },
    update: (id, body) => {
      return axiosInstance.put(`${host}${path.clients}/${id}`, body);
    },
    delete: id => {
      return axiosInstance.delete(`${host}${path.clients}/${id}`);
    },
    activate: id => {
      return axiosInstance.put(`${host}${path.clients}/activate/${id}`);
    },
    inactivate: id => {
      return axiosInstance.put(`${host}${path.clients}/inactivate/${id}`);
    },
  },

  // Batches (Lotes)
  batch: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.batches}`, { params });
    },
    get: id => {
      return axiosInstance.get(`${host}${path.batches}/${id}`);
    },
    create: body => {
      return axiosInstance.post(`${host}${path.batches}`, body);
    },
    update: (id, body) => {
      return axiosInstance.put(`${host}${path.batches}/${id}`, body);
    },
    delete: (id, params) => {
      return axiosInstance.delete(`${host}${path.batches}/${id}`, { params });
    },
    statistics: (params) => {
      return axiosInstance.get(`${host}${path.batches}/statistics/summary`, { params });
    },
    // Mini-dashboard do lote: comprado vs utilizado (aba "Resumo" do detalhe do lote)
    getYield: (id) => {
      return axiosInstance.get(`${host}${path.batches}/${id}/yield`);
    },
    generateLabels: (id, quantity = 1) => {
      return axiosInstance.post(
        `${host}${path.batches}/${id}/labels`,
        { quantity },
        { 
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
    },
    // Anexos de lotes
    listAttachments: (batchId) => {
      return axiosInstance.get(`${host}${path.attachments}/batch/${batchId}`);
    },
    uploadAttachment: (batchId, formData) => {
      return axiosInstance.post(`${host}${path.attachments}/batch/${batchId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    deleteAttachment: (attachmentId) => {
      return axiosInstance.delete(`${host}${path.attachments}/batch-attachment/${attachmentId}`);
    },
    downloadAttachment: (token) => {
      return `${host}${path.attachments}/download/${token}`;
    },
  },

  // Dashboard
  dashboard: {
    getKpis: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/kpis`, { params });
    },
    getTimeEvolution: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/time-evolution`, { params });
    },
    getTopClientsByBatches: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/top-clients-batches`, { params });
    },
    getTopClientsByDrums: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/top-clients-drums`, { params });
    },
    getDistributionByState: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/distribution-state`, { params });
    },
    getDistributionByWeekday: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/distribution-weekday`, { params });
    },
    getDistributionByHour: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/distribution-hour`, { params });
    },
    getMonthlyComparison: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/monthly-comparison`, { params });
    },
    getAvgDrumsByClient: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/avg-drums-by-client`, { params });
    },
    // Estoque e operacional (Admin)
    getStockSummary: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/stock-summary`, { params });
    },
    getProcessVsDrain: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/process-vs-drain`, { params });
    },
    getWithdrawalsSummary: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/withdrawals-summary`, { params });
    },
    getRcHistory: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/rc-history`, { params });
    },
    getStockDaily: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/stock-daily`, { params });
    },
    getDailyYield: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/daily-yield`, { params });
    },
    getBatchYield: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/batch-yield`, { params });
    },
    getRcDaily: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/rc-daily`, { params });
    },
    getTodayTotals: () => {
      return axiosInstance.get(`${host}${path.dashboard}/today-totals`);
    },
    // Relatório de perdas por dia/máquina (zeramentos + residual não fechado).
    getDailyLosses: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/daily-losses`, { params });
    },
    getMachinesDailyBalance: () => {
      return axiosInstance.get(`${host}${path.dashboard}/machines-daily-balance`);
    },
    getDailyBreakdown: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/daily-breakdown`, { params });
    },
  },

  // Tambores (Drums)
  drum: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.drums}`, { params });
    },
    getByUid: (uid) => {
      return axiosInstance.get(`${host}${path.drums}/by-uid/${uid}`);
    },
    // Tambores conferidos/processados por data (tela de correção do PWA).
    // Params: { date: 'YYYY-MM-DD' } (padrão: hoje no backend).
    listForEdit: (params) => {
      return axiosInstance.get(`${host}${path.drums}/for-edit`, { params });
    },
    // Tambores PROCESSADOS por dia, com filtro opcional por máquina (PWA).
    // Params: { date: 'YYYY-MM-DD', machineId } (data padrão: hoje no backend).
    listProcessedByMachine: (params) => {
      return axiosInstance.get(`${host}${path.drums}/processed-by-machine`, { params });
    },
    get: (id) => {
      return axiosInstance.get(`${host}${path.drums}/${id}`);
    },
    history: (id, params) => {
      return axiosInstance.get(`${host}${path.drums}/${id}/history`, { params });
    },
    conference: (id, body) => {
      return axiosInstance.post(`${host}${path.drums}/${id}/conference`, body);
    },
    updatePurchase: (id, body) => {
      return axiosInstance.patch(`${host}${path.drums}/${id}/purchase`, body);
    },
    process: (id, body) => {
      return axiosInstance.post(`${host}${path.drums}/${id}/process`, body);
    },
    updateProcessing: (id, body) => {
      return axiosInstance.patch(`${host}${path.drums}/${id}/processing`, body);
    },
    generateForBatch: (batchId) => {
      return axiosInstance.post(`${host}${path.drums}/batch/${batchId}/generate`);
    },
    // Reimpressão da etiqueta de UM tambor (PDF de 1 página) — mesmo padrão
    // de blob do batch.generateLabels.
    printLabel: (id) => {
      return axiosInstance.post(
        `${host}${path.drums}/${id}/label`,
        null,
        {
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
    },
    delete: (id) => {
      return axiosInstance.delete(`${host}${path.drums}/${id}`);
    },
  },

  // Operações (RC, Descarga, Retirada)
  operation: {
    // RC (Recarga)
    listRc: (params) => {
      return axiosInstance.get(`${host}${path.operations}/rc`, { params });
    },
    getRcToday: () => {
      return axiosInstance.get(`${host}${path.operations}/rc/today`);
    },
    createRc: (body) => {
      return axiosInstance.post(`${host}${path.operations}/rc`, body);
    },
    updateRc: (id, body) => {
      return axiosInstance.patch(`${host}${path.operations}/rc/${id}`, body);
    },
    // Descarga (drainages)
    listDrainages: (params) => {
      return axiosInstance.get(`${host}${path.operations}/drainages`, { params });
    },
    createDrainage: (body) => {
      return axiosInstance.post(`${host}${path.operations}/drainages`, body);
    },
    updateDrainage: (id, body) => {
      return axiosInstance.patch(`${host}${path.operations}/drainages/${id}`, body);
    },
    // body opcional: { justification } — obrigatório para comprador/líder.
    deleteDrainage: (id, body) => {
      return axiosInstance.delete(`${host}${path.operations}/drainages/${id}`, { data: body });
    },
    // Leituras do tanque (tank readings)
    listTankReadings: (params) => {
      return axiosInstance.get(`${host}${path.operations}/tank-readings`, { params });
    },
    listTankReadingsToday: () => {
      return axiosInstance.get(`${host}${path.operations}/tank-readings/today`);
    },
    previewTankReading: (params) => {
      return axiosInstance.get(`${host}${path.operations}/tank-readings/preview`, { params });
    },
    createTankReading: (body) => {
      return axiosInstance.post(`${host}${path.operations}/tank-readings`, body);
    },
    updateTankReading: (id, body) => {
      return axiosInstance.patch(`${host}${path.operations}/tank-readings/${id}`, body);
    },
    // body opcional: { justification } — obrigatório para comprador/líder.
    deleteTankReading: (id, body) => {
      return axiosInstance.delete(`${host}${path.operations}/tank-readings/${id}`, { data: body });
    },
    // Zeramento de máquina/tanque (troca de turno): registra a perda do turno.
    listMachineResets: (params) => {
      return axiosInstance.get(`${host}${path.operations}/machine-resets`, { params });
    },
    previewMachineReset: (params) => {
      return axiosInstance.get(`${host}${path.operations}/machine-resets/preview`, { params });
    },
    createMachineReset: (body) => {
      return axiosInstance.post(`${host}${path.operations}/machine-resets`, body);
    },
    // body opcional: { justification } — obrigatório para comprador/líder.
    deleteMachineReset: (id, body) => {
      return axiosInstance.delete(`${host}${path.operations}/machine-resets/${id}`, { data: body });
    },
    // Adições de Recarga por máquina (consumo)
    listRcConsumptions: (params) => {
      return axiosInstance.get(`${host}${path.operations}/rc-consumptions`, { params });
    },
    listRcConsumptionsToday: () => {
      return axiosInstance.get(`${host}${path.operations}/rc-consumptions/today`);
    },
    createRcConsumption: (body) => {
      return axiosInstance.post(`${host}${path.operations}/rc-consumptions`, body);
    },
    updateRcConsumption: (id, body) => {
      return axiosInstance.patch(`${host}${path.operations}/rc-consumptions/${id}`, body);
    },
    // body opcional: { justification } — obrigatório para comprador/líder.
    deleteRcConsumption: (id, body) => {
      return axiosInstance.delete(`${host}${path.operations}/rc-consumptions/${id}`, { data: body });
    },
    // Retirada de estoque
    listWithdrawals: (params) => {
      return axiosInstance.get(`${host}${path.operations}/withdrawals`, { params });
    },
    createWithdrawal: (body) => {
      return axiosInstance.post(`${host}${path.operations}/withdrawals`, body);
    },
    updateWithdrawal: (id, body) => {
      return axiosInstance.patch(`${host}${path.operations}/withdrawals/${id}`, body);
    },
    // Amarelamento (SolventTurnover) - solvente limpo que virou amarelo
    listTurnovers: (params) => {
      return axiosInstance.get(`${host}${path.operations}/turnovers`, { params });
    },
    listTurnoversToday: () => {
      return axiosInstance.get(`${host}${path.operations}/turnovers/today`);
    },
    createTurnover: (body) => {
      return axiosInstance.post(`${host}${path.operations}/turnovers`, body);
    },
    updateTurnover: (id, body) => {
      return axiosInstance.patch(`${host}${path.operations}/turnovers/${id}`, body);
    },
    // body opcional: { justification } — obrigatório para comprador/líder.
    deleteTurnover: (id, body) => {
      return axiosInstance.delete(`${host}${path.operations}/turnovers/${id}`, { data: body });
    },
  },

  // Enums
  enums: {
    list: () => {
      return axiosInstance.get(`${host}${path.enums}/`);
    },
    find: name => {
      return axiosInstance.get(`${host}${path.enums}/${name}`);
    },
  },

  // Logs de Auditoria (Admin)
  auditLog: {
    list: (params) => {
      return axiosInstance.get(`${host}/api/audit-logs`, { params });
    },
  },

  // Máquinas
  machine: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.machines}`, { params });
    },
    get: (id) => {
      return axiosInstance.get(`${host}${path.machines}/${id}`);
    },
    getByUid: (uid) => {
      return axiosInstance.get(`${host}${path.machines}/by-uid/${uid}`);
    },
    create: (body) => {
      return axiosInstance.post(`${host}${path.machines}`, body);
    },
    update: (id, body) => {
      return axiosInstance.patch(`${host}${path.machines}/${id}`, body);
    },
    delete: (id) => {
      return axiosInstance.delete(`${host}${path.machines}/${id}`);
    },
  },

  // Leituras de temperatura (termômetro por câmera + IA)
  temperature: {
    // Última leitura com valor. Retorna { data: leitura | null }.
    latest: () => {
      return axiosInstance.get(`${host}${path.temperatureReadings}/latest`);
    },
    list: (params) => {
      return axiosInstance.get(`${host}${path.temperatureReadings}`, { params });
    },
    summary: (params) => {
      return axiosInstance.get(`${host}${path.temperatureReadings}/summary`, { params });
    },
    // Imagem binarizada da leitura (autenticada) — retorna um Blob para exibir sob demanda.
    image: (id) => {
      return axiosInstance.get(`${host}${path.temperatureReadings}/${id}/image`, { responseType: 'blob' });
    },
  },
};

